import {
  Dashboard,
  Description,
  DriveFileRenameOutline,
  ListAlt,
  Logout,
  Person,
} from "@mui/icons-material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import { Button, Typography } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import { OktaAuth } from "@okta/okta-auth-js";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import LoadingButton from "src/components/MUI/LoadingButton";
import AuthService from "src/services/authService";
import { injectAuthUser } from "src/store/actions/auth";
import { IAppState } from "src/store/reducers";
import { AuthState } from "src/store/reducers/auth";

import { getIsMobile, getName, getRecordId } from "@utils/helpers";
import { LStorage } from "@utils/localStorage";

import logo from "@images/new-ognomy-logo.png";

const HeaderBar = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const isMobile = getIsMobile();
  const isMobileApp = LStorage.get("SOURCE_HOST") === "mobile";

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { authUser } = useSelector<IAppState, AuthState>((s) => s.auth);
  const publicPaths = [
    "/",
    "/resmed",
    "/resmed/login",
    "/signInPage",
    "/scheduling",
    "/registration",
    "/resetPasswordPage",
  ];

  const isPublicPath = publicPaths.some((path) => location.pathname === path);

  const patientId = isPublicPath ? null : getRecordId(authUser);
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    AuthService.cleanAuth();
    dispatch(injectAuthUser({} as any));

    const OktaAuthUtils = new OktaAuth({
      clientId: process.env.REACT_APP_OKTA_CLIENT_ID as string,
      issuer: process.env.REACT_APP_OKTA_ISSUER as string,
      scopes: ["openid", "profile", "email"],
      responseType: ["code"],
      pkce: true,
    });

    await OktaAuthUtils.revokeAccessToken();
    await OktaAuthUtils.clearStorage();
    await OktaAuthUtils.closeSession();

    history.push("/signInPage");
  };

  const getHomePath = () => {
    return patientId ? "/patientDashboardPage" : "/";
  };

  const navOptions = [
    {
      label: "Dashboard",
      icon: <Dashboard className="tab-icon" />,
      action: () => {
        history.push("/patientDashboardPage");
        handleClose();
      },
    },
    {
      label: "Profile",
      icon: <Person className="tab-icon" />,
      action: () => {
        history.push("/profile");
        handleClose();
      },
    },
    {
      label: "My Details",
      icon: <ListAlt className="tab-icon" />,
      action: () => {
        history.push("/forms?formType=details");
        handleClose();
      },
    },
    {
      label: "Consent Forms",
      icon: <DriveFileRenameOutline className="tab-icon" />,
      action: () => {
        history.push("/consent");
        handleClose();
      },
    },
    {
      label: "My Files",
      icon: <Description className="tab-icon" />,
      action: () => {
        history.push("/myFiles");
        handleClose();
      },
    },
    {
      label: "Logout",
      icon: <Logout className="tab-icon" />,
      action: () => {
        handleLogout();
        handleClose();
      },
    },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="static"
        sx={{
          backgroundColor: isMobileApp ? "inherit" : "common.white",
          boxShadow: "none",
        }}
      >
        <Toolbar
          sx={{ justifyContent: "space-between", paddingX: 1 }}
          disableGutters
        >
          <a href={getHomePath()}>
            <img src={logo} width={200} alt="Ognomy Logo" />
          </a>
          {patientId && (
            <div>
              <div style={{ display: "flex", alignItems: "center" }}>
                {isMobile ? (
                  <IconButton
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    sx={{ color: "secondary.main", gap: 0.5 }}
                  >
                    <AccountCircle />
                  </IconButton>
                ) : (
                  <Button
                    size="small"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    sx={{ color: "secondary.main", gap: 0.5 }}
                  >
                    <AccountCircle />
                    <Typography
                      variant="body1"
                      sx={{ color: "secondary.main" }}
                    >
                      {getName(authUser)}
                    </Typography>
                  </Button>
                )}
              </div>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                {navOptions.map((option) => (
                  <MenuItem
                    key={option.label}
                    onClick={option.action}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {option.icon}
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          )}

          {!patientId && (
            <LoadingButton
              variant={isMobile ? undefined : "outlined"}
              onClick={() => history.push("/signInPage")}
            >
              {isMobile ? <LoginOutlinedIcon /> : "Patient Login"}
            </LoadingButton>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default HeaderBar;
